import React, { useState, useEffect } from 'react';
import { ChevronDown, ArrowLeft, ArrowRight, Check, List } from 'lucide-react';

export const P = ({ children, className = '' }) => <p className={`text-sm text-light-gray leading-relaxed mb-3 ${className}`}>{children}</p>;
export const UL = ({ items }) => (
  <ul className="space-y-1.5 mb-4">
    {items.map((it, i) => <li key={i} className="text-sm text-light-gray flex gap-2"><span className="text-lime shrink-0">•</span><span>{it}</span></li>)}
  </ul>
);
export const H2 = ({ children }) => <h3 className="text-lg font-bold text-white mt-6 mb-2 first:mt-0">{children}</h3>;
export const PathBox = ({ children }) => (
  <p className="text-sm text-lime bg-black border border-divider rounded-lg p-3 mb-3 leading-relaxed">{children}</p>
);
export const Troubleshoot = ({ items, testId }) => (
  <div className="space-y-3 mb-4" data-testid={testId}>
    {items.map(([k, v]) => (
      <div key={k} className="p-3 rounded-lg bg-black border border-divider">
        <p className="text-sm"><span className="text-orange font-bold">{k}</span> <span className="text-light-gray">{v}</span></p>
      </div>
    ))}
  </div>
);

export const PlaybookShell = ({ storageKey, prefix, title, subtitle, intro, sections }) => {
  const [checks, setChecks] = useState({});
  const [open, setOpen] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    try { setChecks(JSON.parse(localStorage.getItem(storageKey)) || {}); } catch { /* noop */ }
  }, [storageKey]);

  const toggle = (id) => {
    setChecks((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const Checklist = ({ prefix: pfx, items, testId }) => (
    <div className="space-y-2 mb-4" data-testid={testId}>
      {items.map((label, i) => {
        const id = `${pfx}-${i}`;
        return (
          <label key={id} className="flex items-start gap-3 p-2.5 rounded-lg bg-black border border-divider cursor-pointer hover:border-lime/40 transition-colors" data-testid={`${testId}-item-${i}`}>
            <input type="checkbox" checked={!!checks[id]} onChange={() => toggle(id)} className="w-4 h-4 mt-0.5 accent-[#B7F04C] shrink-0" />
            <span className={`text-sm ${checks[id] ? 'text-gray line-through' : 'text-light-gray'}`}>{label}</span>
          </label>
        );
      })}
    </div>
  );

  const openSection = (id, steps) => {
    const next = open === id ? null : id;
    setOpen(next);
    if (next && steps) {
      const firstNotDone = steps.findIndex((_, i) => !checks[`step-${i}`]);
      setStepIdx(firstNotDone === -1 ? steps.length - 1 : firstNotDone);
    }
    if (next) {
      setTimeout(() => {
        document.getElementById(`${prefix}-sec-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    }
  };

  const StepsPanel = ({ steps }) => {
    const total = steps.length;
    const doneCount = steps.filter((_, i) => checks[`step-${i}`]).length;
    const step = steps[stepIdx];
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-bold text-white" data-testid={`${prefix}-step-progress`}>Step {stepIdx + 1} of {total}</p>
          <p className="text-xs text-gray">{doneCount} done</p>
        </div>
        <div className="h-2 rounded-full bg-black mb-5 overflow-hidden">
          <div className="h-full bg-lime transition-all duration-300" style={{ width: `${(doneCount / total) * 100}%` }} />
        </div>

        <div className="space-y-1.5 mb-5">
          {steps.map((s, i) => {
            const done = !!checks[`step-${i}`];
            const current = i === stepIdx;
            return (
              <div key={i} className={`rounded-lg border transition-colors ${current ? 'border-lime bg-black' : 'border-divider bg-black/40'}`}>
                <button
                  type="button"
                  onClick={() => setStepIdx(i)}
                  data-testid={`${prefix}-step-${i}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${done ? 'bg-lime text-black' : current ? 'border-2 border-lime text-lime' : 'border border-divider text-gray'}`}>
                    {done ? <Check size={13} /> : s.label}
                  </span>
                  <span className={`text-sm font-semibold ${current ? 'text-white' : done ? 'text-gray line-through' : 'text-light-gray'}`}>{s.title}</span>
                </button>
                {current && (
                  <div className="px-4 pb-4 pt-1 border-t border-divider/60">
                    <div className="pt-3">{s.content}</div>
                    <label className="flex items-center gap-2.5 p-2.5 rounded-lg bg-card-bg border border-divider cursor-pointer mb-3" data-testid={`${prefix}-step-done`}>
                      <input type="checkbox" checked={done} onChange={() => toggle(`step-${i}`)} className="w-4 h-4 accent-[#B7F04C]" />
                      <span className="text-sm font-semibold text-light-gray">Mark this step done</span>
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={i === 0}
                        onClick={() => setStepIdx(i - 1)}
                        data-testid={`${prefix}-step-back`}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-divider text-sm font-bold text-light-gray disabled:opacity-40 hover:border-lime/40 transition-colors"
                      >
                        <ArrowLeft size={14} /> Back
                      </button>
                      {i < total - 1 ? (
                        <button
                          type="button"
                          onClick={() => { if (!done) toggle(`step-${i}`); setStepIdx(i + 1); }}
                          data-testid={`${prefix}-step-next`}
                          className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-lime text-black text-sm font-bold hover:brightness-95 transition-all"
                        >
                          Done — Next Step <ArrowRight size={14} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { if (!done) toggle(`step-${i}`); }}
                          data-testid={`${prefix}-step-finish`}
                          className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-lime text-black text-sm font-bold hover:brightness-95 transition-all"
                        >
                          <Check size={14} /> Finish
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div data-testid={`${prefix}-playbook`}>
      <h1 className="text-3xl font-black text-white mb-1">{title}</h1>
      <p className="text-lg text-gray italic mb-4">{subtitle}</p>
      <P>{intro}</P>

      {/* Mobile jump menu */}
      <details className="lg:hidden mb-5 rounded-xl border border-divider bg-card-bg">
        <summary className="flex items-center gap-2 px-4 py-3 text-sm font-bold text-white cursor-pointer">
          <List size={15} className="text-lime" /> Jump to a section
        </summary>
        <div className="px-4 pb-3 space-y-1">
          {sections.map((s) => (
            <button key={s.id} type="button" onClick={() => openSection(s.id, s.steps)}
              className="block w-full text-left text-sm text-light-gray py-1.5 hover:text-lime">
              {s.title}
            </button>
          ))}
        </div>
      </details>

      <div className="lg:flex lg:gap-8">
        {/* Sticky TOC */}
        <aside className="hidden lg:block w-60 shrink-0">
          <nav className="sticky top-6 rounded-xl border border-divider bg-card-bg p-4" data-testid={`${prefix}-toc`}>
            <p className="text-xs font-bold uppercase tracking-wider text-gray mb-3">In this playbook</p>
            <div className="space-y-1">
              {sections.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => openSection(s.id, s.steps)}
                  data-testid={`${prefix}-toc-${s.id}`}
                  className={`block w-full text-left text-sm py-1.5 px-2 rounded-md transition-colors ${open === s.id ? 'bg-lime/10 text-lime font-bold' : 'text-light-gray hover:text-white'}`}
                >
                  <span className="text-gray mr-1.5">{i + 1}.</span>{s.title}
                </button>
              ))}
            </div>
          </nav>
        </aside>

        {/* Accordion */}
        <div className="flex-1 min-w-0 space-y-3">
          {sections.map((s) => {
            const isOpen = open === s.id;
            const stepsDone = s.steps ? s.steps.filter((_, i) => checks[`step-${i}`]).length : null;
            return (
              <div key={s.id} id={`${prefix}-sec-${s.id}`} className="rounded-xl border border-divider bg-card-bg overflow-hidden scroll-mt-4" data-testid={`${prefix}-section-${s.id}`}>
                <button
                  type="button"
                  onClick={() => openSection(s.id, s.steps)}
                  data-testid={`${prefix}-section-${s.id}-toggle`}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-black/20 transition-colors"
                >
                  <div>
                    <p className="text-base font-bold text-white">{s.title}</p>
                    {s.hint && <p className="text-xs text-gray mt-0.5">{s.hint}</p>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {s.steps && <span className="text-xs font-bold text-gray">{stepsDone}/{s.steps.length} done</span>}
                    <ChevronDown size={18} className={`text-lime transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 border-t border-divider pt-4">
                    {s.steps ? <StepsPanel steps={s.steps} /> : s.content({ Checklist })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
